// What the owner sees of the note the app adds to his message to Prime when an
// artifact beside the thread changed (assistant.ArtifactNote in Go). Prime gets
// the whole sentence; the owner gets the page names and who, in a line.

/** What the app adds to a message to Prime when an artifact changed (assistant.ArtifactNotePrefix in Go). */
export const ARTIFACT_NOTE = "\n\nNote from alfieOS (not the owner's words): "

/**
 * A message to Prime split into the owner's words and the app's note after
 * them ('' when there is none): the note is never shown as his words.
 */
export const splitArtifactNote = (text: string): { words: string; note: string } => {
  const i = text.indexOf(ARTIFACT_NOTE)
  return i < 0
    ? { words: text, note: '' }
    : { words: text.slice(0, i), note: text.slice(i + ARTIFACT_NOTE.length) }
}

/** One page in the note: its title and who changed it ('' when not named). */
export type NotedChange = { title: string; by: string }

// Go writes each page as %q (page <id>[, edited by <who>])
const PAGE_RE = /"((?:[^"\\]|\\.)*)" \(page [0-9a-f-]{36}(?:, edited by ([^)]+))?\)/g

const unquote = (s: string): string => {
  try {
    return JSON.parse(`"${s}"`) as string
  } catch {
    return s
  }
}

/** The pages a note names, in its order. */
export const parseArtifactNote = (note: string): NotedChange[] =>
  [...note.matchAll(PAGE_RE)].map((m) => ({ title: unquote(m[1] ?? ''), by: (m[2] ?? '').trim() }))

const and = (xs: string[]): string =>
  xs.length <= 1 ? (xs[0] ?? '') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`

/**
 * The owner's line: "you edited Launch plan", "Keira edited Shopping",
 * "Notes changed", joined with semicolons. `me` is the owner's first name, so
 * his own edits read as "you". '' when the note names no page.
 */
export const artifactNoteLine = (changes: NotedChange[], me: string): string => {
  const groups = new Map<string, string[]>()
  for (const c of changes) {
    const who = !c.by ? '' : c.by === 'the owner' || (me && c.by === me) ? 'you' : c.by
    groups.set(who, [...(groups.get(who) ?? []), c.title])
  }
  return [...groups]
    .map(([who, titles]) => (who ? `${who} edited ${and(titles)}` : `${and(titles)} changed`))
    .join('; ')
}
